import {
  EventsListUrlState,
  FinishedOrUnfinishedEvents,
  EventsListResponse,
  EventFacetName,
} from "@libry-content/common";
import { createContext, ReactNode, useContext } from "react";
import { DateFilter, useEventsListUrlState } from "./useEventListUrlState";
import { ResolvedEventFacets, usePaginatedEvents } from "./usePaginatedEvents";

export type EventsListContextInterface = Partial<Omit<EventsListResponse, "facets">> & {
  isValidating?: boolean;
  toggleFacet: (facet: EventFacetName, value: string) => void;
  setDateFilter: (dateFilter: DateFilter, date: string | undefined) => void;
  loadMoreEvents: () => void;
  urlState?: EventsListUrlState;
  facets?: ResolvedEventFacets;
  finishedOrUnfinishedEvents?: FinishedOrUnfinishedEvents;
};

const EventsListContext = createContext<EventsListContextInterface>({
  toggleFacet: () => null,
  loadMoreEvents: () => null,
  setDateFilter: () => null,
});

export const useEventsListContext = () => useContext(EventsListContext);

export type EventsListContextProviderProps = {
  children: ReactNode;
  initialData: EventsListResponse;
  finishedOrUnfinishedEvents: FinishedOrUnfinishedEvents;
};

export const EventsListContextProvider = ({
  children,
  initialData,
  finishedOrUnfinishedEvents,
}: EventsListContextProviderProps) => {
  const { urlState, toggleFacet, setDateFilter, loadMoreEvents } = useEventsListUrlState();
  const { events, facets, endOfResults, isValidating } = usePaginatedEvents({
    initialData,
    urlState,
    finishedOrUnfinishedEvents,
  });

  return (
    <EventsListContext.Provider
      value={{
        events,
        toggleFacet,
        setDateFilter,
        loadMoreEvents,
        urlState,
        facets,
        endOfResults,
        isValidating,
        finishedOrUnfinishedEvents,
      }}
    >
      {children}
    </EventsListContext.Provider>
  );
};
